import { useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import {
  IconButton,
  IconButtonProps,
} from '@/components/form/baseInputs/Button/IconButton';
import { DuplicateIcon } from '@/components/icons/DuplicateIcon';
import { Tooltip, TooltipProps } from '@/components/poppers/Tooltip/Tooltip';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';

import {
  getDuplicateTargetKind,
  getDuplicateTargetLabel,
} from './MarkSuggestionAsDuplicate.utils';
import { MarkSuggestionAsDuplicateModal } from './MarkSuggestionAsDuplicateModal';

interface MarkSuggestionAsDuplicateActionProps {
  suggestion: AiSuggestionForReviewCard;
  disabled: boolean;
  onMarkedDuplicate: (suggestionID: string) => void;
  beginOperation?: () => boolean;
  endOperation?: () => void;
  iconProps?: IconButtonProps['iconProps'];
  buttonSx?: IconButtonProps['sx'];
  label?: string;
  tooltipPlacement?: TooltipProps['placement'];
}

export function MarkSuggestionAsDuplicateAction({
  suggestion,
  disabled,
  onMarkedDuplicate,
  beginOperation,
  endOperation,
  iconProps,
  buttonSx,
  label,
  tooltipPlacement = 'top',
}: MarkSuggestionAsDuplicateActionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const targetKind = getDuplicateTargetKind(suggestion);

  if (!targetKind) {
    return null;
  }

  const actionButton = label ? (
    <Button
      startIcon={DuplicateIcon}
      iconProps={iconProps}
      size="xs"
      variant="transparent"
      disabled={disabled}
      aria-label={label}
      onClick={() => setIsModalOpen(true)}
      sx={buttonSx}
    >
      {label}
    </Button>
  ) : (
    <IconButton
      icon={DuplicateIcon}
      iconProps={iconProps}
      size="xs"
      variant="transparent"
      disabled={disabled}
      ariaLabel="Mark suggestion as duplicate"
      onClick={() => setIsModalOpen(true)}
      sx={buttonSx}
    />
  );

  return (
    <>
      <Tooltip
        title={`Mark as duplicate of existing ${getDuplicateTargetLabel(
          targetKind
        )}. All suggestions will be merged.`}
        placement={tooltipPlacement}
      >
        {actionButton}
      </Tooltip>
      {isModalOpen && (
        <MarkSuggestionAsDuplicateModal
          suggestion={suggestion}
          targetKind={targetKind}
          onClose={() => setIsModalOpen(false)}
          onMarkedDuplicate={onMarkedDuplicate}
          beginOperation={beginOperation}
          endOperation={endOperation}
        />
      )}
    </>
  );
}
